<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category, ContentType, Tag, Thumbnails } from '@/models/content'

const props = defineProps({
  contentType: {
    type: String as PropType<ContentType>,
    default: 'article',
    validator(value: string) {
      return ['article', 'chart', 'video'].includes(value)
    },
  },
  slug: {
    type: [String, Number],
    required: true,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  duration: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  excerpt: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  pillsLimit: {
    type: Number,
    default: 2,
  },
  coverImages: {
    type: Object as PropType<{
      coverUrl: string
      thumbnails?: Thumbnails
    }>,
    required: true,
  },
  variant: {
    type: String as PropType<'primary' | 'secondary'>,
    default: 'primary',
  },
  verticallyOrientation: {
    type: Boolean,
    default: false,
  },
})

const limitedPills = computed(() => props.pills.slice(0, props.pillsLimit))

const formattedDuration = useFormattedDuration(
  props.contentType,
  props.duration,
)

const href = useContentHref(props.contentType, props.slug)

const thumbnail =
  props.coverImages.thumbnails?.squareUrl || props.coverImages.coverUrl
const isFeaturedImage = !props.coverImages.thumbnails?.squareUrl
</script>

<template>
  <BaseLink
    :href="href"
    as="a"
    variant="unstyled"
    class="card-hover-lg"
    data-testid="data-visualisation-article-card"
  >
    <article
      class="h-full"
      :class="{
        'bg-s-900': variant === 'primary',
        'bg-warm-gray': variant === 'secondary',
      }"
    >
      <div class="relative bg-white pb-[93%]">
        <img
          :src="thumbnail"
          class="absolute h-full w-full object-center"
          :class="isFeaturedImage ? 'object-cover' : 'object-contain px-2'"
          alt="thumbnail"
          width="300"
          height="400"
        />

        <div class="absolute top-0 h-full w-full border-2 border-black/10" />
      </div>

      <div class="p-5 lg:p-8">
        <p class="text-xs font-semibold text-s-600">
          {{ publishedDate }} · {{ formattedDuration }}
        </p>
        <h1
          class="mt-2 line-clamp-3 text-lg font-semibold leading-snug md:text-3xl md:font-bold"
          :class="[
            {
              'text-white': variant === 'primary',
              'text-black': variant === 'secondary',
            },
          ]"
          :title="title"
          data-testid="data-visualisation-article-card-title"
        >
          {{ title }}
        </h1>
        <div
          class="mt-3"
          :class="
            !verticallyOrientation &&
            'mt-2 justify-between navigation-adjustment:flex'
          "
        >
          <div
            class="custom-line-clamp text-base navigation-adjustment:max-w-3xl"
            :class="[
              {
                'text-white': variant === 'primary',
                'text-black': variant === 'secondary',
                'leading-7': !verticallyOrientation,
              },
            ]"
            v-html="excerpt"
          />
          <div class="mt-6 flex flex-shrink-0 flex-wrap items-end gap-2">
            <LinkedPill
              v-for="pill in limitedPills"
              :key="pill.id"
              :type="pill.__typename"
              :slug="pill.slug"
              variant="yellow"
            >
              {{ pill.name }}
            </LinkedPill>
          </div>
        </div>
      </div>
    </article>
  </BaseLink>
</template>
